import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DefaultApiService from "../../../../../infrastructure/api/default";
import CustomerRequestLocationService from "../../../../../infrastructure/api/customerRequestLocation";
import { formatNumber, formatCurrency, wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import {
  getDerivedLoanFormValues,
  resolvePlantLoanPlan,
} from "../../../../../infrastructure/utils/plantLoanPlans";
import PlantLoanSummaryCard from "../../../../components/loan/PlantLoanSummaryCard";
import {
  ReviewDocumentStatus,
  ReviewFieldGrid,
  ReviewSectionCard,
} from "../../../../components/shared/request-review/RequestReviewPanels";
import RequestFormStepper from "../../../../components/shared/request-review/RequestFormStepper";
import { getVisitorIdForRequest } from "../../../../../infrastructure/utils/referralTracking";
import { getContractorVisitorIdForRequest } from "../../../../../infrastructure/utils/contractorReferralTracking";
import MapPicker from "../../../../components/map/MapPicker";
import MapLinkInput from "../../../../components/shared/MapLinkInput";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import {
  buildRequestEnumSubmitFields,
  normalizeCustomerRequestForForm,
} from "../../shared/checkNewRequest/checkNewRequestPageUtils.js";
import LoanGuaranteeChequeIntentSelector from "../../shared/customerRequest/LoanGuaranteeChequeIntentSelector";
import { initialRegistrationConditions } from "../../shared/customerRequest/initialRegistrationConditionsConstants";

/*
  توضیحات کلی:
  این کامپوننت یک فرم چندمرحله‌ای برای ایجاد/ویرایش درخواست نیروگاه است.
  - از Formik برای مدیریت state فرم استفاده می‌شود.
  - اعتبارسنجی با Yup به صورت مرحله‌ای انجام می‌شود (هر مرحله اسکیمای جداگانه دارد).
  - validateOnChange و validateOnBlur غیرفعال شده‌اند تا اعتبارسنجی بصورت دستی
    در onSubmit انجام شود و کاربران هنگام تلاش برای رفتن به مرحله بعد پیام مناسبی ببینند.
  - فیلد مربوط به "سند مالکیت" (homeOwnerShipDocUrl) به درخواست کاربر حذف شد؛
    بنابراین هیچ ارجاع ورودی/اسکیما/آپلودری در این فایل باقی نمانده است.
*/

const powerPlantTypeOptions = [
  { name: "متصل به شبکه", value: "1" },
  { name: "منفصل از شبکه", value: "2" },
  { name: "هیبریدی", value: "3" },
  { name: "هیبریدی بدون باطری", value: "4" },
];

const locationUsageTypeOptions = [
  { name: "مسکونی", value: "1" },
  { name: "تجاری", value: "2" },
  { name: "اداری", value: "3" },
  { name: "صنعتی", value: "4" },
  { name: "کشاورزی", value: "5" },
  { name: "باغ و ویلا", value: "6" },
  { name: "مسجد / مذهبی", value: "7" },
  { name: "آموزشی", value: "8" },
  { name: "سایر", value: "9" },
];

const ownershipStatusOptions = [
  { name: "مالک هستم", value: "1" },
  { name: "دارای مجوز استفاده", value: "3" },
  { name: "نیاز به بررسی", value: "4" },
];

const platformAcquaintanceSourceOptions = [
  { name: "مهرسان", value: "1" },
  { name: "شبکه های اجتماعی", value: "2" },
  { name: "دوستان", value: "3" },
  { name: "سرچ گوگل", value: "4" },
  { name: "سایر", value: "5" },
];

const applicantGoalOptions = [
  { label: "فروش برق به شبکه", value: 1 },
  { label: "کاهش هزینه برق", value: 2 },
  { label: "برق اضطراری", value: 4 },
  { label: "استقلال از شبکه", value: 8 },
  { label: "سرمایه‌گذاری", value: 16 },
  { label: "نیاز به مشاوره", value: 32 },
];

/** حداقل ۴۰ متر مربع برای هر ۵ کیلووات ظرفیت */
const MIN_INSTALLABLE_AREA_PER_KW = 8;

const getMinInstallableAreaForCapacity = (plantCapacity) => {
  const kw = Number(plantCapacity);
  if (!kw || Number.isNaN(kw)) return null;
  return kw * MIN_INSTALLABLE_AREA_PER_KW;
};

const getMinAreaValidationMessage = (plantCapacity) => {
  const min = getMinInstallableAreaForCapacity(plantCapacity);
  if (!min) return "مساحت فضای قابل نصب برای ظرفیت انتخاب‌شده کافی نیست.";
  return `برای نیروگاه ${Number(plantCapacity)} کیلوواتی، حداقل ${min} متر مربع فضای قابل نصب لازم است.`;
};

const getPowerPlantTypeLabel = (powerPlantType) => {
  const labelMap = {
    1: "متصل به شبکه",
    2: "منفصل از شبکه",
    3: "هیبریدی",
    4: "هیبریدی بدون باطری",
    "1": "متصل به شبکه",
    "2": "منفصل از شبکه",
    "3": "هیبریدی",
    "4": "هیبریدی بدون باطری",
  };
  return labelMap[powerPlantType] || "ثبت نشده";
};

const getLocationUsageTypeLabel = (locationUsageType) => {
  const labelMap = {
    1: "مسکونی",
    2: "تجاری",
    3: "اداری",
    4: "صنعتی",
    5: "کشاورزی",
    6: "باغ و ویلا",
    7: "مسجد / مذهبی",
    8: "آموزشی",
    9: "سایر",
    "1": "مسکونی",
    "2": "تجاری",
    "3": "اداری",
    "4": "صنعتی",
    "5": "کشاورزی",
    "6": "باغ و ویلا",
    "7": "مسجد / مذهبی",
    "8": "آموزشی",
    "9": "سایر",
  };
  return labelMap[locationUsageType] || "ثبت نشده";
};

const getOwnershipStatusLabel = (ownershipStatus) => {
  const labelMap = {
    1: "مالک هستم",
    2: "مستأجر هستم",
    3: "دارای مجوز استفاده",
    4: "نیاز به بررسی",
    "1": "مالک هستم",
    "2": "مستأجر هستم",
    "3": "دارای مجوز استفاده",
    "4": "نیاز به بررسی",
  };
  return labelMap[ownershipStatus] || "ثبت نشده";
};

const getPlatformAcquaintanceSourceLabel = (platformAcquaintanceSource) => {
  const labelMap = {
    1: "مهرسان",
    2: "شبکه های اجتماعی",
    3: "دوستان",
    4: "سرچ گوگل",
    5: "سایر",
    "1": "مهرسان",
    "2": "شبکه های اجتماعی",
    "3": "دوستان",
    "4": "سرچ گوگل",
    "5": "سایر",
  };
  return labelMap[platformAcquaintanceSource] || "ثبت نشده";
};

const getApplicantGoalsLabel = (flags) => {
  if (!flags) return "ثبت نشده";
  const numericFlags = Number(flags);
  if (!numericFlags) return "ثبت نشده";
  const selectedGoals = applicantGoalOptions
    .filter((goal) => (numericFlags & goal.value) === goal.value)
    .map((goal) => goal.label);
  return selectedGoals.length ? selectedGoals.join("، ") : "ثبت نشده";
};

// استپ‌های فرم
const steps = [
  "شرایط احداث",
  "مشخصات نیروگاه و قبض برق",
  "اطلاعات مکانی و آدرس",
  "بارگذاری مدارک",
  "بررسی و ارسال",
];

const stepShortLabels = [
  "شرایط احداث",
  "ظرفیت، نوع و قبض",
  "موقعیت و آدرس",
  "تصاویر و مدارک",
  "مرور و ثبت",
];

const INITIAL_CONDITIONS_ERROR_MESSAGE =
  "برای ادامه، مطالعه و تأیید شرایط احداث الزامی است.";

// اسکیماهای اعتبارسنجی برای هر مرحله
const step0ValidationSchema = Yup.object().shape({
  initialConditionsAccepted: Yup.boolean()
    .oneOf([true], INITIAL_CONDITIONS_ERROR_MESSAGE)
    .required(INITIAL_CONDITIONS_ERROR_MESSAGE),
});

const step1ValidationSchema = Yup.object().shape({
  powerPlantType: Yup.string()
    .required("لطفا نوع نیروگاه را انتخاب کنید.")
    .test(
      "loan-power-plant-type",
      "برای دریافت وام، نوع نیروگاه باید متصل به شبکه، هیبریدی یا هیبریدی بدون باطری باشد.",
      function validateLoanPowerPlantType(val) {
        if (!this.parent.needsLoan) return true;
        return resolvePlantLoanPlan(val).available;
      }
    ),
  locationUsageType: Yup.string().required("لطفا کاربری محل نصب را انتخاب کنید."),
  plantCapacity: Yup.string().required("لطفا این فیلد را پر کنید."),
  installableAreaSquareMeter: Yup.number()
    .typeError("مساحت فضای قابل نصب باید عدد باشد.")
    .min(1, "مساحت باید بزرگتر از صفر باشد.")
    .required("لطفا مساحت فضای قابل نصب را وارد کنید.")
    .test("min-area-for-capacity", function (value) {
      const { plantCapacity } = this.parent;
      const minArea = getMinInstallableAreaForCapacity(plantCapacity);
      if (minArea == null || value == null || value === "") return true;
      if (Number(value) >= minArea) return true;
      return this.createError({
        message: getMinAreaValidationMessage(plantCapacity),
      });
    }),

  electricBoillNumber: Yup.string()
    .required("شناسه قبض برق الزامی است")
    .matches(/^\d{13}$/, "شناسه قبض باید دقیقاً ۱۳ رقم باشد"),

  installationType: Yup.string().required("لطفا نوع محل نصب را انتخاب کنید."),

  unitCount: Yup.number().when("installationType", {
    is: "Apartment",
    then: () =>
      Yup.number()
        .required("تعداد واحدها الزامی است.")
        .min(1, "تعداد واحدها باید حداقل 1 باشد."),
    otherwise: () => Yup.number().nullable(),
  }),

  needsLoan: Yup.boolean()
    .required("لطفا نیاز به وام را مشخص کنید.")
    .test(
      "loan-plan-available",
      "برای نیروگاه آفگرید در حال حاضر وام فعال نیست.",
      function validateLoanAvailability(value) {
        if (!value) return true;
        const { powerPlantType } = this.parent;
        if (!powerPlantType) return true;
        return resolvePlantLoanPlan(powerPlantType).available;
      }
    ),
  loanGuaranteeChequeIntent: Yup.mixed().when("needsLoan", {
    is: true,
    then: () =>
      Yup.string()
        .required("لطفاً وضعیت چک صیادی را مشخص کنید.")
        .oneOf(["1", "2", "3"], "گزینه وضعیت چک صیادی نامعتبر است."),
    otherwise: () => Yup.mixed().nullable(),
  }),
  guarantorFirstName: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () => Yup.string().trim().required("نام ضامن الزامی است."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
  guarantorLastName: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () => Yup.string().trim().required("نام خانوادگی ضامن الزامی است."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
  guarantorNationalCode: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () =>
        Yup.string()
          .required("کد ملی ضامن الزامی است.")
          .matches(/^\d{10}$/, "کد ملی ضامن باید ۱۰ رقم باشد."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
  guarantorMobile: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () =>
        Yup.string()
          .required("موبایل ضامن الزامی است.")
          .matches(/^09\d{9}$/, "موبایل ضامن باید ۱۱ رقم و با ۰۹ شروع شود."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
});


const step2ValidationSchema = Yup.object().shape({
  customerType: Yup.string().required("لطفا این فیلد را پر کنید."),
  phone: Yup.string()
    .matches(/^[0-9+\-\s()]+$/, "لطفا شماره تلفن معتبر وارد کنید.")
    .min(10, "شماره تلفن باید حداقل ۱۰ رقم باشد.")
    .required("لطفا این فیلد را پر کنید."),

  // فیلدهای شرطی بر اساس نوع شخصیت
  ...Yup.object().when("customerType", {
    is: "Individual",
    then: () =>
      Yup.object().shape({
        fullName: Yup.string().required("نام کامل الزامی است."),
      }),
    otherwise: () =>
      Yup.object().shape({
        companyRegisterationCode: Yup.string().required(
          "شماره ثبت شرکت الزامی است."
        ),
        representativeFullName: Yup.string().required(
          "نام کامل نماینده الزامی است."
        ),
      }),
  }).fields,
});

function filterCitiesByProvince(cityList, provinceId) {
  if (!provinceId || !cityList?.length) return [];
  const provinceIdNum = Number(provinceId);
  const provinceIdStr = String(provinceId);
  return cityList.filter((city) => {
    const cityProvinceId = city.provinceId;
    if (cityProvinceId == null || cityProvinceId === "") return false;
    const cityProvinceIdNum = Number(cityProvinceId);
    if (!Number.isNaN(cityProvinceIdNum) && !Number.isNaN(provinceIdNum)) {
      return cityProvinceIdNum === provinceIdNum;
    }
    return String(cityProvinceId) === provinceIdStr;
  });
}

function resolveCitySelectValue(cityId, provinceId, cityList) {
  if (!cityId || !provinceId) return "";
  const cityIdStr = String(cityId);
  const filtered = filterCitiesByProvince(cityList, provinceId);
  const exists = filtered.some(
    (c) => String(c.value) === cityIdStr || Number(c.value) === Number(cityId)
  );
  return exists ? cityIdStr : "";
}

function findListOptionLabel(list, id, valueKey = "value", labelKey = "label") {
  if (id == null || id === "" || !list?.length) return null;
  const idStr = String(id);
  const item = list.find(
    (entry) =>
      String(entry[valueKey]) === idStr || Number(entry[valueKey]) === Number(id)
  );
  return item?.[labelKey] ?? item?.name ?? null;
}

function getProvinceLabel(provinceId, provinceList) {
  return findListOptionLabel(provinceList, provinceId);
}

function getCityLabel(cityId, provinceId, cityList) {
  if (cityId == null || cityId === "" || !cityList?.length) return null;
  const idStr = String(cityId);
  const scoped = provinceId
    ? filterCitiesByProvince(cityList, provinceId)
    : cityList;
  const item =
    scoped.find(
      (c) => String(c.value) === idStr || Number(c.value) === Number(cityId)
    ) ??
    cityList.find(
      (c) => String(c.value) === idStr || Number(c.value) === Number(cityId)
    );
  return item?.label ?? item?.name ?? null;
}

const step3ValidationSchema = Yup.object().shape({
  xPoint: Yup.number().required("لطفا این فیلد را پر کنید."),
  yPoint: Yup.number().required("لطفا این فیلد را پر کنید."),
  provinceId: Yup.string().required("لطفا استان را انتخاب کنید."),
  cityId: Yup.string().required("لطفا شهرستان را انتخاب کنید."),
  address: Yup.string().required("لطفا این فیلد را پر کنید."),
  postalCode: Yup.string()
    .required("لطفا کد پستی را وارد کنید.")
    .matches(/^[0-9]{10}$/, "کد پستی باید دقیقاً 10 رقم باشد."),

  // فیلدهای شرطی برای مدارک آپارتمانی
  ...Yup.object().when("installationType", {
    is: "Apartment",
    then: () =>
      Yup.object().shape({
        boardConsentDocumentUrl: Yup.string().required(
          "بارگذاری رضایت‌نامه هیئت‌مدیره الزامی است."
        ),
        authorizedRepresentativeLetterUrl: Yup.string().required(
          "بارگذاری نامه معرفی نماینده الزامی است."
        ),
        agreementDocumentUrl: Yup.string().required(
          "بارگذاری تفاهم‌نامه همکاری الزامی است."
        ),
      }),
    otherwise: () => Yup.object().shape({}),
  }).fields,
});

const ELECTRIC_BILL_FILE_ACCEPT = "image/*,application/pdf";

const step4ValidationSchema = Yup.object().shape({
  electricBoillUrl: Yup.string().required(
    "لطفا تصویر یا فایل PDF قبض برق را بارگذاری کنید."
  ),
  plantInstallImage1Url: Yup.string().required(
    "بارگذاری عکس اول محل نصب الزامی است."
  ),
  plantInstallImage2Url: Yup.string().required(
    "بارگذاری عکس دوم محل نصب الزامی است."
  ),

  // فیلدهای شرطی برای آپلود مدارک
  ...Yup.object().when("customerType", {
    is: "Individual",
    then: () =>
      Yup.object().shape({
        nationalCardUrl: Yup.string().required("تصویر کارت ملی الزامی است."),
        birthCertificateUrl: Yup.string().required(
          "تصویر شناسنامه الزامی است."
        ),
      }),
    otherwise: () => Yup.object().shape({}),
  }).fields,

  // اعتبارسنجی مدارک آپارتمانی در مرحله بارگذاری مدارک
  ...Yup.object().when("installationType", {
    is: "Apartment",
    then: () =>
      Yup.object().shape({
        boardConsentDocumentUrl: Yup.string().required(
          "برای درخواست آپارتمانی، بارگذاری رضایت‌نامه هیئت‌مدیره اجباری است."
        ),
        authorizedRepresentativeLetterUrl: Yup.string().required(
          "برای درخواست آپارتمانی، بارگذاری نامه معرفی نماینده اجباری است."
        ),
        agreementDocumentUrl: Yup.string().required(
          "برای درخواست آپارتمانی، بارگذاری تفاهم‌نامه همکاری اجباری است."
        ),
      }),
    otherwise: () => Yup.object().shape({}),
  }).fields,
});

const step5ValidationSchema = Yup.object().shape({
  // مرحله نهایی - همه فیلدها باید پر باشد (شامل مدارک)
  ...step0ValidationSchema.fields,
  ...step1ValidationSchema.fields,
  ...step2ValidationSchema.fields,
  ...step3ValidationSchema.fields,
  ...step4ValidationSchema.fields,
});

const CustomerRequestModifyPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const data = useLoaderData();
  const normalizedRequest = normalizeCustomerRequestForForm(data.request);
  let navigate = useNavigate();
  const { id: requestId } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState(data.provinces || []);
  const [cities, setCities] = useState(data.cities || []);

  useEffect(() => {
    if (data.provinces?.length) setProvinces(data.provinces);
    if (data.cities?.length) setCities(data.cities);
  }, [data.provinces, data.cities]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const latest =
          await CustomerRequestLocationService.loadCustomerRequestLocationLists();
        if (cancelled) return;
        if (latest.provinces?.length) setProvinces(latest.provinces);
        if (latest.cities?.length) setCities(latest.cities);
      } catch (e) {
        console.error("Failed to load provinces/cities for request form", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const [activeStep, setActiveStep] = useState(0);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [tempMapPosition, setTempMapPosition] = useState([32.68, 51.68]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const formikRef = useRef(null);

  const onNavigateToListPage = () => {
    navigate("/SLN/Customer/Requests");
  };

  // وقتی provinceId در initial values تنظیم می‌شود، مطمئن شو که مقدار فرم درست است
  useEffect(() => {
    if (data.request?.provinceId && formikRef.current?.setFieldValue) {
      // فقط برای اطمینان از اینکه provinceId به string تبدیل شده است
      const provinceIdStr = String(data.request.provinceId);
      // کمی تاخیر برای اطمینان از اینکه Formik initialize شده است
      setTimeout(() => {
        if (formikRef.current?.setFieldValue && formikRef.current?.values) {
          const currentProvinceId = String(formikRef.current.values.provinceId || "");
          if (currentProvinceId !== provinceIdStr) {
            formikRef.current.setFieldValue("provinceId", provinceIdStr, false);
          }
        }
      }, 100);
    }
  }, [data.request?.provinceId]);

  useEffect(() => {
    if (!cities.length || !formikRef.current?.values) return;
    const { provinceId, cityId } = formikRef.current.values;
    if (!cityId) return;

    const resolved = resolveCitySelectValue(cityId, provinceId, cities);
    if (!resolved && formikRef.current.setFieldValue) {
      formikRef.current.setFieldValue("cityId", "", false);
    }
  }, [cities]);

  const resolveSubmitLocationIds = async (formValue, { forceRefresh = false } = {}) => {
    let provinceList = provinces;
    let cityList = cities;

    if (!forceRefresh) {
      const resolvedFromState = CustomerRequestLocationService.resolveLocationIdsForSubmit(
        formValue.provinceId,
        formValue.cityId,
        provinceList,
        cityList
      );

      if (resolvedFromState.valid) {
        return { ...resolvedFromState, provinceList, cityList };
      }
    }

    try {
      const latest =
        await CustomerRequestLocationService.loadCustomerRequestLocationLists();
      if (latest.provinces?.length) {
        provinceList = latest.provinces;
        setProvinces(latest.provinces);
      }
      if (latest.cities?.length) {
        cityList = latest.cities;
        setCities(latest.cities);
      }
    } catch (e) {
      console.error("Failed to refresh location lists before submit", e);
    }

    const resolved = CustomerRequestLocationService.resolveLocationIdsForSubmit(
      formValue.provinceId,
      formValue.cityId,
      provinceList,
      cityList
    );

    return { ...resolved, provinceList, cityList };
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // انتخاب اسکیمای اعتبارسنجی بر اساس مرحله فعلی
  const getValidationSchema = () => {
    switch (activeStep) {
      case 0:
        return step0ValidationSchema;
      case 1:
        return step1ValidationSchema;
      // activeStep 2 => اطلاعات مکانی (مختصات)
      case 2:
        return step3ValidationSchema;
      // activeStep 3 => بارگذاری مدارک
      case 3:
        return step4ValidationSchema;
      // activeStep 4 => بررسی نهایی (ترکیبی)
      case 4:
        return step5ValidationSchema;
      default:
        return step0ValidationSchema;
    }
  };

  // Submit Action
  const onSubmit = async (
    formValue,
    { setSubmitting, validateForm, setTouched, setErrors }
  ) => {
    // اگر در مرحله آخر نیستیم، فقط به مرحله بعد برویم
    if (activeStep < steps.length - 1) {
      // اعتبارسنجی فقط فیلدهای مرحله فعلی
      const currentStepSchema = getValidationSchema();

      // لاگ و تبدیل مقادیر مختصات به عدد برای جلوگیری از مشکل validation
      if (activeStep === 2) {
        const coerced = {
          ...formValue,
          xPoint: formValue.xPoint
            ? Number(formValue.xPoint)
            : formValue.xPoint,
          yPoint: formValue.yPoint
            ? Number(formValue.yPoint)
            : formValue.yPoint,
        };
        formValue = coerced;
      }

      // لاگ مقدار واقعی فیلدهای مدارک
      try {
        await currentStepSchema.validate(formValue, { abortEarly: false });

        if (activeStep === 2) {
          const resolvedLocation =
            await resolveSubmitLocationIds(formValue);
          if (!resolvedLocation.valid) {
            const locationError =
              "شهر انتخاب شده نامعتبر است. لطفاً استان و شهرستان را مجدداً انتخاب کنید.";
            toast.error(locationError);
            setErrors({ cityId: locationError, provinceId: locationError });
            setTouched({ cityId: true, provinceId: true });
            setSubmitting(false);
            return;
          }
          if (formikRef.current?.setFieldValue) {
            formikRef.current.setFieldValue(
              "provinceId",
              String(resolvedLocation.provinceId)
            );
            formikRef.current.setFieldValue(
              "cityId",
              String(resolvedLocation.cityId)
            );
          }
        }

        // اگر validation موفق بود، به مرحله بعد بروید
        handleNext();
        setSubmitting(false);
        return;
      } catch (validationErrors) {
        // validation errors را پردازش کن
        const errors = {};
        const touched = {};

        validationErrors.inner?.forEach((error) => {
          errors[error.path] = error.message;
          touched[error.path] = true;
        });

        // برای درخواست‌های آپارتمانی، فیلدهای مدارک را هم به عنوان touched علامت‌گذاری کن
        // در مرحله 1 (اطلاعات مکانی) و مرحله 2 (بارگذاری مدارک)
        if (formValue.installationType === "Apartment" && (activeStep === 2 || activeStep === 3)) {
          if (!formValue.boardConsentDocumentUrl) {
            touched.boardConsentDocumentUrl = true;
            errors.boardConsentDocumentUrl = errors.boardConsentDocumentUrl || "بارگذاری رضایت‌نامه هیئت‌مدیره الزامی است.";
          }
          if (!formValue.authorizedRepresentativeLetterUrl) {
            touched.authorizedRepresentativeLetterUrl = true;
            errors.authorizedRepresentativeLetterUrl = errors.authorizedRepresentativeLetterUrl || "بارگذاری نامه معرفی نماینده الزامی است.";
          }
          if (!formValue.agreementDocumentUrl) {
            touched.agreementDocumentUrl = true;
            errors.agreementDocumentUrl = errors.agreementDocumentUrl || "بارگذاری تفاهم‌نامه همکاری الزامی است.";
          }
        }

        // mark fields as touched
        setTouched(touched);

        // Set errors in Formik
        setErrors(errors);

        setSubmitting(false);
        return;
      }
    }

    // مرحله نهایی - ارسال به سرور
    setLoading(true);
    const locationIds = await resolveSubmitLocationIds(formValue, {
      forceRefresh: true,
    });

    if (!locationIds.valid) {
      const locationError =
        "شهر انتخاب شده نامعتبر است. لطفاً استان و شهرستان را مجدداً انتخاب کنید.";
      toast.error(locationError);
      setErrors({ cityId: locationError, provinceId: locationError, submit: locationError });
      setTouched({ cityId: true, provinceId: true, submit: true });
      setLoading(false);
      setSubmitting(false);
      setActiveStep(1);
      return;
    }

    const enumSubmitFields = buildRequestEnumSubmitFields(formValue);

    const sendData = {
      ...formValue,
      visitorId: getVisitorIdForRequest(),
      contractorVisitorId: getContractorVisitorIdForRequest(),
      ...enumSubmitFields,
      cityId: locationIds.cityId,
      provinceId: locationIds.provinceId,
      CityId: locationIds.cityId,
      ProvinceId: locationIds.provinceId,
      NeedsLoan: Boolean(formValue.needsLoan),
    };

    delete sendData.cityId;
    delete sendData.provinceId;
    delete sendData.loanGuaranteeChequeIntent;
    delete sendData.loanGuaranteeChequeIntentTitle;
    delete sendData.guarantorFirstName;
    delete sendData.guarantorLastName;
    delete sendData.guarantorNationalCode;
    delete sendData.guarantorMobile;

    if (formValue.needsLoan && String(formValue.loanGuaranteeChequeIntent) === "2") {
      sendData.GuarantorFirstName = String(formValue.guarantorFirstName || "").trim();
      sendData.GuarantorLastName = String(formValue.guarantorLastName || "").trim();
      sendData.GuarantorNationalCode = String(formValue.guarantorNationalCode || "").replace(/\D/g, "");
      sendData.GuarantorMobile = String(formValue.guarantorMobile || "").replace(/\D/g, "");
      sendData.guarantorFirstName = sendData.GuarantorFirstName;
      sendData.guarantorLastName = sendData.GuarantorLastName;
      sendData.guarantorNationalCode = sendData.GuarantorNationalCode;
      sendData.guarantorMobile = sendData.GuarantorMobile;
    }

    if (formValue.needsLoan) {
      const loanPlan = resolvePlantLoanPlan(formValue.powerPlantType);
      if (loanPlan.available) {
        const derived = getDerivedLoanFormValues(formValue.powerPlantType);
        sendData.note = sendData.note
          ? `${sendData.note}\n[وام] طرح ${derived.loanPlanType} — سقف ${formatNumber(loanPlan.maxLoanAmount)} ریال، ${formatNumber(loanPlan.months)} قسط، قسط ماهانه ${formatNumber(loanPlan.monthlyInstallment)} ریال`
          : `[وام] طرح ${derived.loanPlanType} — سقف ${formatNumber(loanPlan.maxLoanAmount)} ریال، ${formatNumber(loanPlan.months)} قسط، قسط ماهانه ${formatNumber(loanPlan.monthlyInstallment)} ریال`;
      }
    }

    delete sendData.requestedLoanAmount;
    delete sendData.loanInstallmentMonths;
    delete sendData.loanPlanType;
    delete sendData.initialConditionsAccepted;

    try {
      const res = await DefaultApiService.modifyApi(
        "SLN/Customer/Requests",
        sendData
      );

      if (res.isSuccess) {
        setSubmissionResult({
          success: true,
          message: res.message,
        });
        setIsSubmitted(true);
      } else {
        const errorMessage = res.message || "خطا در ارسال درخواست";
        toast.error(errorMessage);
        setErrors({ submit: errorMessage });
        setTouched({ submit: true });
      }

      setLoading(false);
    } catch (error) {
      console.error("Customer request submit failed:", error);

      // بررسی خطای عدم تکمیل پروفایل یا سایر خطاهای 400 از سرور
      const serverMessage = error?.response?.data?.message;
      if (error?.response?.status === 400 && serverMessage) {
        toast.error(serverMessage);
        setErrors({ submit: serverMessage });
        setTouched({ submit: true });
        setLoading(false);
        setSubmitting(false);
        return;
      }

      // نمایش خطاهای validation از سرور در فیلدها
      const errorList = error?.response?.data;
      const serverErrors = {};
      const serverTouched = {};

      if (typeof errorList === "object") {
        for (const key in errorList) {
          if (Object.hasOwnProperty.call(errorList, key)) {
            const list = errorList[key];
            serverErrors[key] = Array.isArray(list) ? list[0] : list;
            serverTouched[key] = true;
          }
        }
      } else {
        serverErrors.submit = "خطایی رخ داده است.";
        serverTouched.submit = true;
      }

      setErrors(serverErrors);
      setTouched(serverTouched);

      setLoading(false);
      setSubmitting(false);
    }
  };

  // اگر درخواست ارسال شده، صفحه نتیجه را نمایش بده
  if (isSubmitted && submissionResult) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <BreadcrumbsComponent />
        </Grid>

        <Grid item xs={12} md={12}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 6 }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: "success.main",
                  mb: 2,
                }}
              />

              <Typography variant="h4" gutterBottom color="success.main">
                درخواست شما با موفقیت ثبت شد
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                {submissionResult.message}
              </Typography>

              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  onClick={onNavigateToListPage}
                  sx={{ minWidth: 200 }}
                >
                  بازگشت به لیست درخواست‌ها
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, md: 3 }}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card sx={{ overflow: "visible" }}>
          <CardHeader
            title={
              requestId !== "create"
                ? `ویرایش درخواست نیروگاه ${data?.request?.fullName}`
                : "ثبت درخواست نیروگاه"
            }
            titleTypographyProps={{
              variant: isMobile ? "subtitle1" : "h6",
              fontWeight: 700,
            }}
            action={
              isMobile ? null : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "fit-content",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="gray"
                    startIcon={<UndoIcon />}
                    onClick={onNavigateToListPage}
                  >
                    بازگشت
                  </Button>
                </Box>
              )
            }
            sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}
          />

          <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 0, pb: { xs: 2, md: 3 } }}>
            <RequestFormStepper
              activeStep={activeStep}
              steps={steps}
              stepShortLabels={stepShortLabels}
              isMobile={isMobile}
            />

            <Formik
              initialValues={{
                ...normalizedRequest,
                customerType: normalizedRequest?.customerType || "Individual",
                provinceId: normalizedRequest?.provinceId
                  ? String(normalizedRequest.provinceId)
                  : "",
                cityId: normalizedRequest?.cityId ? String(normalizedRequest.cityId) : "",
                installationType: normalizedRequest?.installationType || "Villa",
                unitCount: normalizedRequest?.unitCount || null,
                powerPlantType: normalizedRequest?.powerPlantType ?? "",
                locationUsageType: normalizedRequest?.locationUsageType ?? "",
                installableAreaSquareMeter:
                  normalizedRequest?.installableAreaSquareMeter != null
                    ? normalizedRequest.installableAreaSquareMeter
                    : "",
                ownershipStatus: normalizedRequest?.ownershipStatus ?? "",
                platformAcquaintanceSource:
                  normalizedRequest?.platformAcquaintanceSource ?? "",
                applicantGoalFlags:
                  normalizedRequest?.applicantGoalFlags != null
                    ? Number(normalizedRequest.applicantGoalFlags)
                    : 0,
                plantKind: "IndividualBranching", // همیشه انشعابی انفرادی
                needsLoan: normalizedRequest?.needsLoan || false,
                loanGuaranteeChequeIntent:
                  normalizedRequest?.loanGuaranteeChequeIntent != null
                    ? String(normalizedRequest.loanGuaranteeChequeIntent)
                    : "",
                guarantorFirstName: "",
                guarantorLastName: "",
                guarantorNationalCode: "",
                guarantorMobile: "",
                loanPlanType: null,
                requestedLoanAmount: null,
                loanInstallmentMonths: null,
                electricBoillUrl:
                  typeof normalizedRequest?.electricBoillUrl === "string"
                    ? normalizedRequest.electricBoillUrl
                    : "",
                plantInstallImage1Url:
                  typeof normalizedRequest?.plantInstallImage1Url === "string"
                    ? normalizedRequest.plantInstallImage1Url
                    : "",
                plantInstallImage2Url:
                  typeof normalizedRequest?.plantInstallImage2Url === "string"
                    ? normalizedRequest.plantInstallImage2Url
                    : "",
                nationalCardUrl:
                  typeof normalizedRequest?.nationalCardUrl === "string"
                    ? normalizedRequest.nationalCardUrl
                    : "",
                birthCertificateUrl:
                  typeof normalizedRequest?.birthCertificateUrl === "string"
                    ? normalizedRequest.birthCertificateUrl
                    : "",
                boardConsentDocumentUrl:
                  typeof normalizedRequest?.boardConsentDocumentUrl === "string"
                    ? normalizedRequest.boardConsentDocumentUrl
                    : "",
                authorizedRepresentativeLetterUrl:
                  typeof normalizedRequest?.authorizedRepresentativeLetterUrl ===
                  "string"
                    ? normalizedRequest.authorizedRepresentativeLetterUrl
                    : "",
                agreementDocumentUrl:
                  typeof normalizedRequest?.agreementDocumentUrl === "string"
                    ? normalizedRequest.agreementDocumentUrl
                    : "",
                initialConditionsAccepted: false,
              }}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={onSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldTouched,
                isSubmitting,
                setErrors,
                setFieldValue,
                setTouched,
                validateForm,
              }) => {
                // ذخیره setFieldValue در ref برای دسترسی خارج از context
                formikRef.current = { setFieldValue, values };

                const fieldSize = isMobile ? "small" : "medium";

                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  size: fieldSize,
                  value: values[name] || "",
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                const onMakeLocationFormikVariable = (name) => ({
                  ...onMakeFormikVariable(name),
                  size: "small",
                });

                const locationStepTypography = {
                  sectionTitle: {
                    fontSize: { xs: "0.8125rem", md: "0.875rem" },
                    fontWeight: 600,
                    mb: { xs: 0.5, md: 0.75 },
                  },
                  fieldLabel: {
                    "& .MuiInputBase-root": {
                      fontSize: { xs: "0.75rem", md: "0.8125rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.75rem", md: "0.8125rem" },
                    },
                    "& .MuiSelect-select": {
                      fontSize: { xs: "0.75rem", md: "0.8125rem" },
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: "0.6875rem",
                    },
                  },
                  docLabel: {
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", md: "0.8125rem" },
                  },
                };

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                      {/* فیلدهای پنهان */}
                      <TextField
                        onChange={wrapFormikHandleChange(handleChange)}
                        {...onMakeFormikVariable("companyId")}
                        sx={{ display: "none" }}
                      />

                      <TextField
                        onChange={wrapFormikHandleChange(handleChange)}
                        {...onMakeFormikVariable("stepInfoId")}
                        sx={{ display: "none" }}
                      />

                      {/* مرحله ۱: شرایط احداث */}
                      {activeStep === 0 && (
                        <Grid
                          item
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: "100%", md: "70%" },
                              maxWidth: { xs: "100%", md: "70%" },
                              mx: "auto",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.1),
                              bgcolor: "#ffffff",
                              backgroundImage: (theme) =>
                                `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${alpha(theme.palette.primary.main, 0.025)} 100%)`,
                              boxShadow: (theme) =>
                                `0 4px 20px ${alpha(theme.palette.common.black, 0.04)}`,
                            }}
                          >
                            <Box
                              sx={{
                                px: { xs: 2, sm: 2.5, md: 3 },
                                py: { xs: 2.25, sm: 2.75, md: 3 },
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1.25}
                                alignItems="flex-start"
                                sx={{ mb: { xs: 2, md: 2.5 } }}
                              >
                                <Box
                                  sx={{
                                    mt: 0.15,
                                    width: { xs: 34, md: 38 },
                                    height: { xs: 34, md: 38 },
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: (theme) =>
                                      alpha(theme.palette.primary.main, 0.06),
                                    color: "primary.main",
                                    flexShrink: 0,
                                  }}
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                                </Box>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={700}
                                  sx={{
                                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                                    lineHeight: 1.55,
                                  }}
                                >
                                  شرایط احداث نیروگاه خورشیدی
                                </Typography>
                              </Stack>

                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                  mb: 2,
                                  lineHeight: 1.85,
                                  fontSize: { xs: "0.875rem", md: "0.9375rem" },
                                }}
                              >
                                متقاضی گرامی، لطفاً قبل از ادامه ثبت درخواست، شرایط
                                اولیه زیر را با دقت مطالعه فرمایید. ادامه فرایند
                                ثبت‌نام به منزله آگاهی و تأیید اولیه شما نسبت به این
                                شرایط است.
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 1.5,
                                  fontWeight: 600,
                                  fontSize: { xs: "0.875rem", md: "0.9375rem" },
                                }}
                              >
                                برای ثبت درخواست احداث نیروگاه خورشیدی، لازم است
                                شرایط زیر را داشته باشید:
                              </Typography>
                              <Box
                                component="ol"
                                sx={{
                                  m: 0,
                                  pr: { xs: 2.25, md: 3 },
                                  pl: { xs: 0.5, md: 1 },
                                  mb: 2.5,
                                  "& li": {
                                    mb: 1,
                                    lineHeight: 1.85,
                                    fontSize: { xs: "0.875rem", md: "0.9375rem" },
                                    color: "text.primary",
                                  },
                                }}
                              >
                                {initialRegistrationConditions.map((condition) => (
                                  <Box component="li" key={condition}>
                                    {condition}
                                  </Box>
                                ))}
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mb: 2.5,
                                  lineHeight: 1.8,
                                  fontSize: { xs: "0.8125rem", md: "0.875rem" },
                                }}
                              >
                                بدیهی است ثبت درخواست به معنای تأیید نهایی پروژه نیست
                                و اطلاعات و مدارک متقاضی در مراحل بعدی توسط کارشناسان
                                سولارنت بررسی خواهد شد.
                              </Typography>

                              <Box
                                sx={{
                                  px: { xs: 1.5, sm: 2, md: 2.25 },
                                  py: { xs: 1.5, sm: 1.75, md: 2 },
                                  borderRadius: 1.5,
                                  bgcolor: "#fafafa",
                                  border: "1px solid",
                                  borderColor: (theme) =>
                                    alpha(theme.palette.divider, 0.9),
                                }}
                              >
                                <FormControl
                                  error={
                                    Boolean(
                                      errors.initialConditionsAccepted &&
                                        touched.initialConditionsAccepted
                                    )
                                  }
                                  component="fieldset"
                                  variant="standard"
                                  fullWidth
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={Boolean(values.initialConditionsAccepted)}
                                        onChange={(event) => {
                                          setFieldValue(
                                            "initialConditionsAccepted",
                                            event.target.checked
                                          );
                                          if (event.target.checked) {
                                            setFieldTouched(
                                              "initialConditionsAccepted",
                                              false,
                                              false
                                            );
                                          }
                                        }}
                                        onBlur={() =>
                                          setFieldTouched(
                                            "initialConditionsAccepted",
                                            true,
                                            false
                                          )
                                        }
                                        color="primary"
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" sx={{ lineHeight: 1.75 }}>
                                        اینجانب شرایط احداث نیروگاه خورشیدی را مطالعه
                                        کرده و از الزامات آن آگاه هستم.
                                      </Typography>
                                    }
                                    sx={{ alignItems: "flex-start", m: 0 }}
                                  />
                                  {errors.initialConditionsAccepted &&
                                  touched.initialConditionsAccepted ? (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ mt: 1, display: "block" }}
                                    >
                                      {errors.initialConditionsAccepted}
                                    </Typography>
                                  ) : null}
                                </FormControl>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* مرحله ۲: مشخصات نیروگاه و قبض برق */}
                      {activeStep === 1 && (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{
                                fontSize: { xs: "1rem", md: "1.25rem" },
                                mb: { xs: 0.5, md: 1 },
                              }}
                            >
                              مشخصات نیروگاه و قبض برق
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={6} lg={4}>
                            <DropdownPicker
                              label="ظرفیت نیروگاه"
                              onChange={async (event) => {
                                const nextCapacity = event.target.value;
                                await setFieldValue("plantCapacity", nextCapacity);
                                if (
                                  values.installableAreaSquareMeter !== "" &&
                                  values.installableAreaSquareMeter != null
                                ) {
                                  setFieldTouched(
                                    "installableAreaSquareMeter",
                                    true,
                                    true
                                  );
                                }
                              }}
                              onBlur={handleBlur}
                              list={data.plantCapacity}
                              filterable={false}
                              {...onMakeFormikVariable("plantCapacity")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6} lg={4}>
                            <DropdownPicker
                              label="نوع نیروگاه"
                              onChange={(event) => {
                                const nextType = event.target.value;
                                setFieldValue("powerPlantType", nextType);
                                setFieldTouched("powerPlantType", true, false);
                                const derived = getDerivedLoanFormValues(nextType);
                                setFieldValue("loanPlanType", derived.loanPlanType);
                                setFieldValue(
                                  "requestedLoanAmount",
                                  derived.requestedLoanAmount
                                );
                                setFieldValue(
                                  "loanInstallmentMonths",
                                  derived.loanInstallmentMonths
                                );
                                if (!resolvePlantLoanPlan(nextType).available) {
                                  setFieldValue("needsLoan", false);
                                }
                              }}
                              onBlur={() => setFieldTouched("powerPlantType", true)}
                              list={powerPlantTypeOptions}
                              filterable={false}
                              {...onMakeFormikVariable("powerPlantType")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6} lg={4}>
                            <DropdownPicker
                              label="کاربری محل نصب"
                              onChange={(event) => {
                                setFieldValue("locationUsageType", event.target.value);
                              }}
                              onBlur={handleBlur}
                              list={locationUsageTypeOptions}
                              filterable={true}
                              {...onMakeFormikVariable("locationUsageType")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6} lg={4}>
                            <TextField
                              label="مساحت فضای قابل نصب نیروگاه"
                              type="number"
                              onChange={(e) =>
                                setFieldValue(
                                  "installableAreaSquareMeter",
                                  e.target.value.replace(/[^\d.]/g, "")
                                )
                              }
                              onBlur={() =>
                                setFieldTouched("installableAreaSquareMeter", true)
                              }
                              inputProps={{
                                min: getMinInstallableAreaForCapacity(values.plantCapacity) ?? 1,
                                inputMode: "decimal",
                              }}
                              placeholder="مثال: 120"
                              helperText={
                                touched.installableAreaSquareMeter &&
                                errors.installableAreaSquareMeter
                                  ? errors.installableAreaSquareMeter
                                  : getMinInstallableAreaForCapacity(values.plantCapacity)
                                    ? `حداقل ${getMinInstallableAreaForCapacity(values.plantCapacity)} متر مربع برای نیروگاه ${values.plantCapacity} کیلوواتی`
                                    : "متر مربع فضای آزاد (حداقل ۴۰ متر برای نیروگاه ۵ کیلوواتی)"
                              }
                              error={
                                touched.installableAreaSquareMeter &&
                                Boolean(errors.installableAreaSquareMeter)
                              }
                              value={values.installableAreaSquareMeter ?? ""}
                              fullWidth
                            />
                          </Grid>

                          <Grid item xs={12} md={6} lg={4}>
                            <TextField
                              label="شناسه قبض برق"
                              name="electricBoillNumber"
                              value={values.electricBoillNumber || ""}
                              onChange={(e) => {
                                const onlyDigits = e.target.value.replace(/\D/g, "");
                                setFieldValue("electricBoillNumber", onlyDigits);
                              }}
                              onBlur={() => setFieldTouched("electricBoillNumber", true)}
                              error={Boolean(touched.electricBoillNumber && errors.electricBoillNumber)}
                              helperText={
                                touched.electricBoillNumber && errors.electricBoillNumber
                                  ? errors.electricBoillNumber
                                  : "شناسه ۱۳ رقمی قبض برق"
                              }
                              inputProps={{
                                maxLength: 13,
                                inputMode: "numeric",
                              }}
                              fullWidth
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Accordion
                              defaultExpanded={!isMobile}
                              disableGutters
                              elevation={0}
                              sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: "grey.50",
                                "&:before": { display: "none" },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  px: { xs: 1.5, sm: 2 },
                                  minHeight: { xs: 48, sm: 56 },
                                  "& .MuiAccordionSummary-content": { my: 1 },
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{ fontSize: { xs: "0.9375rem", sm: "1rem" } }}
                                  >
                                    اطلاعات تکمیلی
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    اختیاری — برای ارزیابی دقیق‌تر درخواست شما
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: 2 }}>
                                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                  <Grid item xs={12} md={6} lg={4}>
                                    <DropdownPicker
                                      label="وضعیت مالکیت محل"
                                      onChange={(event) => {
                                        setFieldValue("ownershipStatus", event.target.value);
                                      }}
                                      onBlur={handleBlur}
                                      list={ownershipStatusOptions}
                                      filterable={false}
                                      {...onMakeFormikVariable("ownershipStatus")}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6} lg={4}>
                                    <DropdownPicker
                                      label="نحوه آشنایی با پلتفرم"
                                      onChange={(event) => {
                                        setFieldValue(
                                          "platformAcquaintanceSource",
                                          event.target.value
                                        );
                                      }}
                                      onBlur={handleBlur}
                                      list={platformAcquaintanceSourceOptions}
                                      filterable={false}
                                      {...onMakeFormikVariable("platformAcquaintanceSource")}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <FormControl component="fieldset" fullWidth>
                                      <FormLabel
                                        component="legend"
                                        sx={{ mb: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                                      >
                                        هدف شما از اجرای نیروگاه چیست؟
                                      </FormLabel>
                                      <FormGroup
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: {
                                            xs: "1fr",
                                            sm: "1fr 1fr",
                                            md: "repeat(3, 1fr)",
                                          },
                                          gap: { xs: 0, sm: 0.5 },
                                        }}
                                      >
                                        {applicantGoalOptions.map((goal) => {
                                          const currentFlags = Number(values.applicantGoalFlags) || 0;
                                          const checked = (currentFlags & goal.value) === goal.value;
                                          return (
                                            <FormControlLabel
                                              key={goal.value}
                                              sx={{
                                                m: 0,
                                                alignItems: "flex-start",
                                                "& .MuiFormControlLabel-label": {
                                                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                                  lineHeight: 1.45,
                                                  pt: { xs: 0.75, sm: 0.5 },
                                                },
                                              }}
                                              control={
                                                <Checkbox
                                                  checked={checked}
                                                  onChange={() => {
                                                    const nextFlags = checked
                                                      ? currentFlags & ~goal.value
                                                      : currentFlags | goal.value;
                                                    setFieldValue(
                                                      "applicantGoalFlags",
                                                      nextFlags
                                                    );
                                                  }}
                                                />
                                              }
                                              label={goal.label}
                                            />
                                          );
                                        })}
                                      </FormGroup>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>

                          {/* نوع محل نصب */}
                          <Grid item xs={12} md={6}>
                            <FormControl
                              component="fieldset"
                              error={errors.installationType && touched.installationType}
                            >
                              <FormLabel
                                component="legend"
                                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                              >
                                نوع محل نصب نیروگاه
                              </FormLabel>
                              <RadioGroup
                                row={!isMobile}
                                name="installationType"
                                value={values.installationType || ""}
                                sx={{
                                  flexDirection: { xs: "column", sm: "row" },
                                  gap: { xs: 0, sm: 0 },
                                }}
                                onChange={(event) => {
                                  setFieldValue("installationType", event.target.value);
                                  // اگر ویلایی انتخاب شد، تعداد واحد را null کن
                                  if (event.target.value === "Villa") {
                                    setFieldValue("unitCount", null);
                                  }
                                }}
                              >
                                <FormControlLabel
                                  value="Villa"
                                  control={<Radio />}
                                  label="انشعابی (ویلایی)"
                                />
                                <FormControlLabel
                                  value="Apartment"
                                  control={<Radio />}
                                  label="آپارتمانی"
                                />
                              </RadioGroup>
                              {errors.installationType && touched.installationType && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                                  {errors.installationType}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>

                          {/* تعداد واحدها - فقط برای آپارتمانی */}
                          {values.installationType === "Apartment" && (
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="تعداد واحدهای ساختمان"
                                type="number"
                                onChange={wrapFormikHandleChange(handleChange)}
                                inputProps={{ min: 1 }}
                                {...onMakeFormikVariable("unitCount")}
                              />

                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <PlantLoanSummaryCard
                              powerPlantType={values.powerPlantType}
                            />
                          </Grid>

                          {/* نیاز به وام */}
                          <Grid item xs={12}>
                            <FormControlLabel
                              sx={{
                                alignItems: "flex-start",
                                m: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: { xs: "0.875rem", sm: "1rem" },
                                  lineHeight: 1.5,
                                  pt: 0.25,
                                },
                              }}
                              control={
                                <Switch
                                  checked={values.needsLoan || false}
                                  disabled={
                                    !values.powerPlantType ||
                                    !resolvePlantLoanPlan(values.powerPlantType)
                                      .available
                                  }
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    if (checked && !values.powerPlantType) {
                                      setFieldTouched("powerPlantType", true, true);
                                      return;
                                    }
                                    setFieldValue("needsLoan", checked);
                                    setFieldTouched("needsLoan", true, false);
                                    if (checked) {
                                      const derived = getDerivedLoanFormValues(
                                        values.powerPlantType
                                      );
                                      setFieldValue(
                                        "loanPlanType",
                                        derived.loanPlanType
                                      );
                                      setFieldValue(
                                        "requestedLoanAmount",
                                        derived.requestedLoanAmount
                                      );
                                      setFieldValue(
                                        "loanInstallmentMonths",
                                        derived.loanInstallmentMonths
                                      );
                                    } else {
                                      setFieldValue("loanPlanType", null);
                                      setFieldValue("requestedLoanAmount", null);
                                      setFieldValue("loanInstallmentMonths", null);
                                      setFieldValue("loanGuaranteeChequeIntent", "");
                                      setFieldValue("guarantorFirstName", "");
                                      setFieldValue("guarantorLastName", "");
                                      setFieldValue("guarantorNationalCode", "");
                                      setFieldValue("guarantorMobile", "");
                                    }
                                  }}
                                  name="needsLoan"
                                  color="primary"
                                />
                              }
                              label="آیا نیاز به وام برای احداث نیروگاه دارید؟"
                            />
                            {!values.powerPlantType ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 0.5, pr: 0.5 }}
                              >
                                ابتدا نوع نیروگاه را انتخاب کنید.
                              </Typography>
                            ) : null}
                            {errors.needsLoan && touched.needsLoan ? (
                              <Typography
                                color="error"
                                variant="caption"
                                display="block"
                                sx={{ mt: 0.5 }}
                              >
                                {errors.needsLoan}
                              </Typography>
                            ) : null}
                          </Grid>

                          {values.needsLoan &&
                          resolvePlantLoanPlan(values.powerPlantType).available ? (
                            <Grid item xs={12}>
                              <LoanGuaranteeChequeIntentSelector
                                value={values.loanGuaranteeChequeIntent}
                                onChange={(next) => {
                                  setFieldValue("loanGuaranteeChequeIntent", next);
                                  setFieldTouched("loanGuaranteeChequeIntent", true, false);
                                  if (String(next) !== "2") {
                                    setFieldValue("guarantorFirstName", "");
                                    setFieldValue("guarantorLastName", "");
                                    setFieldValue("guarantorNationalCode", "");
                                    setFieldValue("guarantorMobile", "");
                                  }
                                }}
                                error={errors.loanGuaranteeChequeIntent}
                                touched={touched.loanGuaranteeChequeIntent}
                                formValues={values}
                                formErrors={errors}
                                formTouched={touched}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                              />
                            </Grid>
                          ) : null}
                        </>
                      )}

                      {/* مرحله ۳: اطلاعات مکانی و آدرس */}
                      {activeStep === 2 && (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              sx={locationStepTypography.sectionTitle}
                            >
                              اطلاعات مکانی و آدرس
                            </Typography>
                          </Grid>

                          {/* فیلدهای مخفی برای X و Y */}
                          <TextField
                            type="hidden"
                            {...onMakeLocationFormikVariable("xPoint")}
                            sx={{ display: "none" }}
                          />
                          <TextField
                            type="hidden"
                            {...onMakeLocationFormikVariable("yPoint")}
                            sx={{ display: "none" }}
                          />

                          {/* ورود لینک نقشه */}
                          <Grid item xs={12} sx={locationStepTypography.fieldLabel}>
                            <MapLinkInput
                              size="small"
                              onCoordsResolved={({ xPoint, yPoint }) => {
                                if (formikRef.current) {
                                  formikRef.current.setFieldValue("xPoint", xPoint);
                                  formikRef.current.setFieldValue("yPoint", yPoint);
                                  setTempMapPosition([xPoint, yPoint]);
                                }
                              }}
                            />
                          </Grid>

                          {/* دکمه انتخاب موقعیت با نمایش وضعیت */}
                          <Grid item xs={12} sx={locationStepTypography.fieldLabel}>
                            <Button
                              variant={
                                values.xPoint && values.yPoint
                                  ? "contained"
                                  : "outlined"
                              }
                              color={
                                values.xPoint && values.yPoint
                                  ? "success"
                                  : "primary"
                              }
                              fullWidth
                              size="small"
                              sx={{
                                py: { xs: 0.65, md: 0.75 },
                                fontSize: { xs: "0.75rem", md: "0.8125rem" },
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                // تنظیم موقعیت فعلی در نقشه (اگر مقادیر موجود باشد)
                                if (values.xPoint && values.yPoint) {
                                  setTempMapPosition([
                                    parseFloat(values.xPoint),
                                    parseFloat(values.yPoint),
                                  ]);
                                }
                                setMapPickerOpen(true);
                              }}
                              startIcon={
                                values.xPoint && values.yPoint ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <LocationOnIcon />
                                )
                              }
                            >
                              {values.xPoint && values.yPoint
                                ? "✓ موقعیت انتخاب شده - کلیک برای تغییر"
                                : "📍 انتخاب موقعیت از روی نقشه"}
                            </Button>
                            {errors.xPoint && touched.xPoint && (
                              <Typography
                                color="error"
                                variant="caption"
                                display="block"
                                sx={{ mt: 1, fontSize: "0.6875rem" }}
                              >
                                لطفا موقعیت را از روی نقشه انتخاب کنید
                              </Typography>
                            )}
                            {values.xPoint && values.yPoint && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ mt: 1, textAlign: "center", fontSize: "0.6875rem" }}
                              >
                                مختصات: {parseFloat(values.xPoint).toFixed(6)},{" "}
                                {parseFloat(values.yPoint).toFixed(6)}
                              </Typography>
                            )}
                          </Grid>

                          <Grid item xs={12} md={6} sx={locationStepTypography.fieldLabel}>
                            <DropdownPicker
                              label="استان"
                              onChange={(event) => {
                                const selectedProvinceId = event.target.value;
                                setFieldValue("provinceId", String(selectedProvinceId));
                                setFieldValue("cityId", "");
                              }}
                              onBlur={handleBlur}
                              list={provinces}
                              optionName="label"
                              optionValue="value"
                              filterable={true}
                              {...onMakeLocationFormikVariable("provinceId")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6} sx={locationStepTypography.fieldLabel}>
                            <DropdownPicker
                              label="شهرستان"
                              name="cityId"
                              onChange={(event) => {
                                const nextCityId = String(event.target.value);
                                setFieldValue("cityId", nextCityId);
                                const selectedCity = cities.find(
                                  (c) =>
                                    String(c.value) === nextCityId ||
                                    Number(c.value) === Number(nextCityId)
                                );
                                if (selectedCity?.provinceId != null) {
                                  setFieldValue(
                                    "provinceId",
                                    String(selectedCity.provinceId)
                                  );
                                }
                              }}
                              onBlur={handleBlur}
                              list={filterCitiesByProvince(cities, values.provinceId)}
                              optionName="label"
                              optionValue="value"
                              filterable={true}
                              size="small"
                              disabled={!values.provinceId || cities.length === 0}
                              value={resolveCitySelectValue(
                                values.cityId,
                                values.provinceId,
                                cities
                              )}
                              error={errors.cityId && touched.cityId}
                              helperText={
                                errors.cityId && touched.cityId
                                  ? errors.cityId
                                  : !values.provinceId
                                    ? "ابتدا استان را انتخاب کنید"
                                    : cities.length === 0
                                      ? "در حال بارگذاری شهرستان‌ها..."
                                      : ""
                              }
                            />
                          </Grid>

                          <Grid item xs={12} md={8} sx={locationStepTypography.fieldLabel}>
                            <TextField
                              label="آدرس"
                              type="string"
                              onChange={wrapFormikHandleChange(handleChange)}
                              {...onMakeLocationFormikVariable("address")}
                            />
                          </Grid>

                          <Grid item xs={12} md={4} sx={locationStepTypography.fieldLabel}>
                            <TextField
                              label="کد پستی"
                              onChange={wrapFormikHandleChange(handleChange)}
                              inputProps={{
                                maxLength: 10,
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              placeholder="1234567890"
                              helperText="کد پستی 10 رقمی (بدون خط تیره)"
                              {...onMakeLocationFormikVariable("postalCode")}
                            />
                          </Grid>

                          {/* مدارک آپارتمانی - فقط برای نوع نصب آپارتمانی */}
                          {values.installationType === "Apartment" && (
                            <>
                              <Grid item xs={12}>
                                <Card
                                  sx={{
                                    backgroundColor: "#fff3cd",
                                    border: "1px solid #ffc107",
                                  }}
                                >
                                  <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                                    <Typography
                                      variant="body2"
                                      sx={locationStepTypography.docLabel}
                                    >
                                      ⚠️ برای پروژه‌های آپارتمانی، بارگذاری اسناد
                                      زیر الزامی است:
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                  <Typography variant="body2" sx={locationStepTypography.docLabel}>
                                    رضایت‌نامه هیئت‌مدیره <span style={{ color: "red" }}>*</span>
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<DownloadIcon />}
                                    onClick={async () => {
                                      try {
                                        const response = await DefaultApiService.downloadFile(
                                          "CustomerRequests/DownloadBoardResolution"
                                        );
                                        const url = window.URL.createObjectURL(response);
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.setAttribute("download", "رضایت‌نامه هیئت‌مدیره.pdf");
                                        document.body.appendChild(link);
                                        link.click();
                                        link.parentNode.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                        toast.success("فایل رضایت‌نامه هیئت‌مدیره با موفقیت دانلود شد");
                                      } catch (error) {
                                        console.error("خطا در دانلود فایل:", error);
                                        toast.error("خطا در دانلود فایل تفاهم‌نامه");
                                      }
                                    }}
                                    sx={{ fontSize: "0.7rem", px: 1 }}
                                  >
                                    دانلود فایل
                                  </Button>
                                </Box>
                                <FileUploader
                                  moduleName={"SLN/CustomerData"}
                                  maxFiles={1}
                                  label=""
                                  required
                                  onChange={(value) => {
                                    setFieldValue(
                                      "boardConsentDocumentUrl",
                                      typeof value === "string" ? value : ""
                                    );
                                    setFieldTouched("boardConsentDocumentUrl", true);
                                  }}
                                  baseUrl={data.baseUrl}
                                  placeholder={
                                    "رضایت‌نامه هیئت‌مدیره را انتخاب کنید (اجباری)"
                                  }
                                  {...onMakeLocationFormikVariable(
                                    "boardConsentDocumentUrl"
                                  )}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                  <Typography variant="body2" sx={locationStepTypography.docLabel}>
                                    نامه معرفی نماینده <span style={{ color: "red" }}>*</span>
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<DownloadIcon />}
                                    onClick={async () => {
                                      try {
                                        const response = await DefaultApiService.downloadFile(
                                          "CustomerRequests/DownloadRepresentativeIntroductionLetter"
                                        );
                                        const url = window.URL.createObjectURL(response);
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.setAttribute("download", "نامه معرفی نماینده.pdf");
                                        document.body.appendChild(link);
                                        link.click();
                                        link.parentNode.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                        toast.success("فایل نامه معرفی نماینده با موفقیت دانلود شد");
                                      } catch (error) {
                                        console.error("خطا در دانلود فایل:", error);
                                        toast.error("خطا در دانلود فایل نامه معرفی نماینده");
                                      }
                                    }}
                                    sx={{ fontSize: "0.7rem", px: 1 }}
                                  >
                                    دانلود فایل
                                  </Button>
                                </Box>
                                <FileUploader
                                  moduleName={"SLN/CustomerData"}
                                  maxFiles={1}
                                  label=""
                                  required
                                  onChange={(value) => {
                                    setFieldValue(
                                      "authorizedRepresentativeLetterUrl",
                                      typeof value === "string" ? value : ""
                                    );
                                    setFieldTouched("authorizedRepresentativeLetterUrl", true);
                                  }}
                                  baseUrl={data.baseUrl}
                                  placeholder={
                                    "نامه معرفی نماینده را انتخاب کنید (اجباری)"
                                  }
                                  {...onMakeLocationFormikVariable(
                                    "authorizedRepresentativeLetterUrl"
                                  )}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                  <Typography variant="body2" sx={locationStepTypography.docLabel}>
                                    تفاهم‌نامه همکاری <span style={{ color: "red" }}>*</span>
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<DownloadIcon />}
                                    onClick={async () => {
                                      try {
                                        const response = await DefaultApiService.downloadFile(
                                          "CustomerRequests/DownloadAgreementTemplate"
                                        );
                                        const url = window.URL.createObjectURL(response);
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.setAttribute("download", "تفاهم‌نامه همکاری.pdf");
                                        document.body.appendChild(link);
                                        link.click();
                                        link.parentNode.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                        toast.success("فایل تفاهم‌نامه همکاری با موفقیت دانلود شد");
                                      } catch (error) {
                                        console.error("خطا در دانلود فایل:", error);
                                        toast.error("خطا در دانلود فایل تفاهم‌نامه");
                                      }
                                    }}
                                    sx={{ fontSize: "0.7rem", px: 1 }}
                                  >
                                    دانلود فایل
                                  </Button>
                                </Box>
                                <FileUploader
                                  moduleName={"SLN/CustomerData"}
                                  maxFiles={1}
                                  label=""
                                  required
                                  onChange={(value) => {
                                    setFieldValue(
                                      "agreementDocumentUrl",
                                      typeof value === "string" ? value : ""
                                    );
                                    setFieldTouched("agreementDocumentUrl", true);
                                  }}
                                  baseUrl={data.baseUrl}
                                  placeholder={
                                    "تفاهم‌نامه همکاری تکمیل شده را انتخاب کنید (اجباری)"
                                  }
                                  accept="application/pdf"
                                  {...onMakeLocationFormikVariable(
                                    "agreementDocumentUrl"
                                  )}
                                />
                              </Grid>
                            </>
                          )}
                        </>
                      )}

                      {/* مرحله ۴: بارگذاری مدارک */}
                      {activeStep === 3 && (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{
                                fontSize: { xs: "1rem", md: "1.25rem" },
                                mb: { xs: 0.5, md: 1 },
                              }}
                            >
                              بارگذاری مدارک
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <FileUploader
                              moduleName={"SLN/CustomerData"}
                              maxFiles={1}
                              label="تصویر یا PDF قبض برق"
                              onChange={(value) => {
                                setFieldValue(
                                  "electricBoillUrl",
                                  typeof value === "string" ? value : ""
                                );
                              }}
                              baseUrl={data.baseUrl}
                              placeholder={"تصویر یا PDF قبض برق را انتخاب کنید"}
                              accept={ELECTRIC_BILL_FILE_ACCEPT}
                              {...onMakeFormikVariable("electricBoillUrl")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <FileUploader
                              moduleName={"SLN/CustomerData"}
                              maxFiles={1}
                              label="عکس اول از محل نصب نیروگاه"
                              onChange={(value) => {
                                setFieldValue(
                                  "plantInstallImage1Url",
                                  typeof value === "string" ? value : ""
                                );
                              }}
                              baseUrl={data.baseUrl}
                              placeholder={"عکس محل نصب را انتخاب کنید"}
                              accept="image/*"
                              {...onMakeFormikVariable("plantInstallImage1Url")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <FileUploader
                              moduleName={"SLN/CustomerData"}
                              maxFiles={1}
                              label="عکس دوم از محل نصب نیروگاه"
                              onChange={(value) => {
                                setFieldValue(
                                  "plantInstallImage2Url",
                                  typeof value === "string" ? value : ""
                                );
                              }}
                              baseUrl={data.baseUrl}
                              placeholder={"عکس محل نصب را انتخاب کنید"}
                              accept="image/*"
                              {...onMakeFormikVariable("plantInstallImage2Url")}
                            />
                          </Grid>
                        </>
                      )}

                      {/* مرحله ۵: بررسی نهایی و ارسال */}
                      {activeStep === 4 && (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{ mb: 0.5, fontSize: { xs: "1rem", md: "1.25rem" } }}
                            >
                              بررسی نهایی
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              قبل از ارسال، اطلاعات زیر را مرور کنید.
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <ReviewSectionCard
                              title="مشخصات نیروگاه"
                              icon={<SolarPowerIcon />}
                            >
                              <ReviewFieldGrid
                                fields={[
                                  {
                                    label: "ظرفیت",
                                    value: values.plantCapacity
                                      ? `${values.plantCapacity} کیلووات`
                                      : null,
                                  },
                                  {
                                    label: "نوع نیروگاه",
                                    value: getPowerPlantTypeLabel(values.powerPlantType),
                                  },
                                  {
                                    label: "کاربری محل نصب",
                                    value: getLocationUsageTypeLabel(
                                      values.locationUsageType
                                    ),
                                  },
                                  {
                                    label: "مساحت قابل نصب",
                                    value: values.installableAreaSquareMeter
                                      ? `${values.installableAreaSquareMeter} متر مربع`
                                      : null,
                                  },
                                  {
                                    label: "وضعیت مالکیت",
                                    value: getOwnershipStatusLabel(values.ownershipStatus),
                                  },
                                  {
                                    label: "نحوه آشنایی با پلتفرم",
                                    value: getPlatformAcquaintanceSourceLabel(
                                      values.platformAcquaintanceSource
                                    ),
                                  },
                                  {
                                    label: "هدف متقاضی",
                                    value: getApplicantGoalsLabel(values.applicantGoalFlags),
                                  },
                                  {
                                    label: "نوع شخصیت",
                                    value:
                                      values.customerType === "Individual"
                                        ? "حقیقی"
                                        : values.customerType === "Legal"
                                          ? "حقوقی"
                                          : null,
                                  },
                                  {
                                    label: "شناسه قبض برق",
                                    value: values.electricBoillNumber,
                                  },
                                  {
                                    label: "نوع محل نصب",
                                    value:
                                      values.installationType === "Apartment"
                                        ? "آپارتمانی"
                                        : values.installationType === "Villa"
                                          ? "ویلایی"
                                          : values.installationType,
                                  },
                                  ...(values.installationType === "Apartment"
                                    ? [
                                        {
                                          label: "تعداد واحد",
                                          value: values.unitCount,
                                        },
                                      ]
                                    : []),
                                ]}
                              />
                            </ReviewSectionCard>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <PlantLoanSummaryCard
                              powerPlantType={values.powerPlantType}
                              needsLoan={values.needsLoan}
                            />
                          </Grid>

                          {values.customerType === "Legal" && (
                            <Grid item xs={12} md={6}>
                              <ReviewSectionCard
                                title="اطلاعات شرکت"
                                icon={<HomeIcon />}
                              >
                                <ReviewFieldGrid
                                  fields={[
                                    {
                                      label: "شماره ثبت شرکت",
                                      value: values.companyRegisterationCode,
                                    },
                                  ]}
                                />
                              </ReviewSectionCard>
                            </Grid>
                          )}

                          <Grid item xs={12} md={values.customerType === "Legal" ? 6 : 12}>
                            <ReviewSectionCard
                              title="اطلاعات مکانی"
                              icon={<LocationOnIcon />}
                            >
                              <ReviewFieldGrid
                                fields={[
                                  {
                                    label: "استان",
                                    value: getProvinceLabel(
                                      values.provinceId,
                                      provinces
                                    ),
                                  },
                                  {
                                    label: "شهرستان",
                                    value: getCityLabel(
                                      values.cityId,
                                      values.provinceId,
                                      cities
                                    ),
                                  },
                                  {
                                    label: "آدرس",
                                    value: values.address,
                                  },
                                  {
                                    label: "کد پستی",
                                    value: values.postalCode,
                                  },
                                  {
                                    label: "مختصات (X, Y)",
                                    value:
                                      values.xPoint && values.yPoint
                                        ? `${values.xPoint} ، ${values.yPoint}`
                                        : null,
                                  },
                                ]}
                              />
                            </ReviewSectionCard>
                          </Grid>

                          <Grid item xs={12}>
                            <ReviewSectionCard
                              title="مدارک بارگذاری‌شده"
                              icon={<DescriptionOutlinedIcon />}
                            >
                              <Stack spacing={1.25}>
                                <ReviewDocumentStatus
                                  label="قبض برق"
                                  uploaded={Boolean(values.electricBoillUrl)}
                                />
                                <ReviewDocumentStatus
                                  label="عکس اول محل نصب"
                                  uploaded={Boolean(values.plantInstallImage1Url)}
                                />
                                <ReviewDocumentStatus
                                  label="عکس دوم محل نصب"
                                  uploaded={Boolean(values.plantInstallImage2Url)}
                                />
                              </Stack>
                            </ReviewSectionCard>
                          </Grid>

                          <Grid item xs={12}>
                            <ReviewSectionCard
                              title="توضیحات تکمیلی"
                              icon={<NotesOutlinedIcon />}
                            >
                              <TextField
                                label="توضیحات (اختیاری)"
                                multiline
                                minRows={3}
                                fullWidth
                                placeholder="در صورت نیاز توضیحی برای کارشناس بنویسید..."
                                onChange={wrapFormikHandleChange(handleChange)}
                                {...onMakeFormikVariable("note")}
                              />
                            </ReviewSectionCard>
                          </Grid>
                        </>
                      )}

                      {/* پیام خطای ارسال (مثلاً تکمیل نبودن پروفایل) */}
                      {errors.submit && touched.submit && (
                        <Grid item xs={12}>
                          <Alert severity="error" onClose={() => setTouched({ ...touched, submit: false })}>
                            {errors.submit}
                          </Alert>
                        </Grid>
                      )}

                      {/* دکمه‌های ناوبری */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            position: { xs: "sticky", md: "static" },
                            bottom: { xs: 0, md: "auto" },
                            zIndex: { xs: 2, md: "auto" },
                            bgcolor: { xs: "background.paper", md: "transparent" },
                            borderTop: { xs: 1, md: 0 },
                            borderColor: { xs: "divider", md: "transparent" },
                            mx: { xs: -1.5, sm: -2, md: 0 },
                            px: { xs: 1.5, sm: 2, md: 0 },
                            py: { xs: 1.5, md: 0 },
                            pb: {
                              xs: "calc(12px + env(safe-area-inset-bottom, 0px))",
                              md: 0,
                            },
                            mt: { xs: 1, md: 0 },
                          }}
                        >
                          {isMobile ? (
                            <Stack spacing={1.25}>
                              <Button
                                disabled={
                                  isSubmitting ||
                                  (activeStep === 0 &&
                                    !values.initialConditionsAccepted)
                                }
                                variant="contained"
                                type="submit"
                                size="large"
                                fullWidth
                                sx={{ py: 1.25 }}
                              >
                                {loading && (
                                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                )}
                                {activeStep === steps.length - 1
                                  ? requestId !== "create"
                                    ? "ثبت"
                                    : "ثبت درخواست"
                                  : "بعدی"}
                              </Button>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  disabled={activeStep === 0}
                                  variant="outlined"
                                  size="medium"
                                  color="gray"
                                  onClick={handleBack}
                                  fullWidth
                                >
                                  قبلی
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="medium"
                                  color="gray"
                                  onClick={onNavigateToListPage}
                                  fullWidth
                                >
                                  انصراف
                                </Button>
                              </Stack>
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Button
                                disabled={activeStep === 0}
                                variant="outlined"
                                size="large"
                                color="gray"
                                onClick={handleBack}
                              >
                                قبلی
                              </Button>

                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="large"
                                  color="gray"
                                  onClick={onNavigateToListPage}
                                >
                                  انصراف
                                </Button>

                                <Button
                                  disabled={
                                    isSubmitting ||
                                    (activeStep === 0 &&
                                      !values.initialConditionsAccepted)
                                  }
                                  variant="contained"
                                  type="submit"
                                  size="large"
                                >
                                  {loading && (
                                    <CircularProgress size={16} color="inherit" />
                                  )}
                                  {activeStep === steps.length - 1
                                    ? requestId !== "create"
                                      ? "ثبت"
                                      : "ثبت درخواست"
                                    : "بعدی"}
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>

            {/* MapPicker Dialog */}
            <MapPicker
              open={mapPickerOpen}
              onClose={() => setMapPickerOpen(false)}
              initialPosition={tempMapPosition}
              onSelect={(coords) => {
                // به‌روزرسانی مقادیر X و Y در Formik از طریق ref
                if (formikRef.current) {
                  formikRef.current.setFieldValue("xPoint", coords.lat);
                  formikRef.current.setFieldValue("yPoint", coords.lng);

                  // اگر آدرس دریافت شده باشد، آن را نیز تنظیم کن
                  if (coords.address) {
                    formikRef.current.setFieldValue("address", coords.address);
                  }
                }
                setTempMapPosition([coords.lat, coords.lng]);
              }}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CustomerRequestModifyPage;

